import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getApiBaseUrl } from "../config/apiBase";
import {
  getWhiteLabelLookupHost,
  isDefaultPlatformHost,
  writeBrandCache,
  clearBrandCache,
} from "../utils/whiteLabelBrandCache";

const DEFAULT_BRAND = {
  id: null,
  name: "Ping Pro",
  company_name: "Blutec",
  logo_url: null,
  favicon_url: "/favicon.ico",
  primary_color: "#0064E0",
  accent_color: "#22C55E",
  tagline: "WhatsApp broadcasts, live chat, and automation",
  support_email: "sales@blutec.ai",
};

/** Shown until white-label API completes — no Ping name/logo/favicon. */
const PENDING_BRAND = {
  id: null,
  name: "",
  company_name: "",
  logo_url: null,
  favicon_url: null,
  primary_color: "#0064E0",
  accent_color: "#22C55E",
  tagline: "",
  support_email: "",
};

const BrandingContext = createContext({
  ...PENDING_BRAND,
  isBrandingReady: false,
  isWhiteLabel: false,
  isDefaultHost: false,
  showBrandSkeleton: true,
});

const BLANK_FAVICON = "data:,";

export function clearPingDocumentBrand() {
  document.title = "";
  document.querySelectorAll(
    "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']"
  ).forEach((link) => {
    link.href = BLANK_FAVICON;
  });
}

export function applyDocumentBrand(brand) {
  if (!brand?.name && !brand?.logo_url && !brand?.favicon_url) return;
  if (brand.name) document.title = brand.name;
  document.documentElement.style.setProperty("--blue", brand.primary_color || "#0064E0");
  document.documentElement.style.setProperty("--green", brand.accent_color || "#22C55E");
  const favicon = brand.favicon_url || brand.logo_url;
  if (favicon) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;
    document.querySelectorAll("link[rel='apple-touch-icon']").forEach((el) => {
      el.href = favicon;
    });
  }
}

function resolveSupportEmail(brand) {
  const support = String(brand?.support_email || "").trim();
  const company = String(brand?.company_email || "").trim();
  const defaultInbox = "sales@blutec.ai";
  if (brand?.id) {
    if (support && support.toLowerCase() !== defaultInbox) return support;
    return company;
  }
  return support || company || defaultInbox;
}

function applyWhiteLabelResponse(lookupHost, json, setBrand, setIsWhiteLabel) {
  if (json?.data) {
    const next = { ...DEFAULT_BRAND, ...json.data };
    next.support_email = resolveSupportEmail(next);
    setBrand(next);
    setIsWhiteLabel(true);
    writeBrandCache(lookupHost, next);
    applyDocumentBrand(next);
    return;
  }

  clearBrandCache(lookupHost);

  if (isDefaultPlatformHost(lookupHost)) {
    setBrand(DEFAULT_BRAND);
    setIsWhiteLabel(false);
    applyDocumentBrand(DEFAULT_BRAND);
    return;
  }

  setBrand(PENDING_BRAND);
  setIsWhiteLabel(false);
  clearPingDocumentBrand();
}

export function BrandingProvider({ children }) {
  const lookupHost = getWhiteLabelLookupHost();

  const [brand, setBrand] = useState(PENDING_BRAND);
  const [isBrandingReady, setIsBrandingReady] = useState(false);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);

  useEffect(() => {
    clearPingDocumentBrand();
    setBrand(PENDING_BRAND);
    setIsWhiteLabel(false);
    setIsBrandingReady(false);

    const base = getApiBaseUrl();
    const url = `${base}/api/white-label/public?host=${encodeURIComponent(lookupHost)}`;

    let cancelled = false;

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`White-label API ${res.status}`);
        }
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        applyWhiteLabelResponse(lookupHost, json, setBrand, setIsWhiteLabel);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[BrandingProvider] white-label fetch failed:", err.message);
        clearBrandCache(lookupHost);
        if (isDefaultPlatformHost(lookupHost)) {
          setBrand(DEFAULT_BRAND);
          setIsWhiteLabel(false);
          applyDocumentBrand(DEFAULT_BRAND);
        } else {
          setBrand(PENDING_BRAND);
          setIsWhiteLabel(false);
          clearPingDocumentBrand();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsBrandingReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lookupHost]);

  const isDefaultHost =
    isBrandingReady && !isWhiteLabel && isDefaultPlatformHost(lookupHost);
  const showBrandSkeleton = !isBrandingReady;

  const supportEmail = resolveSupportEmail(brand);

  const value = useMemo(
    () => ({
      ...brand,
      support_email: supportEmail,
      supportEmail,
      isBrandingReady,
      isWhiteLabel,
      isDefaultHost,
      showBrandSkeleton,
    }),
    [brand, supportEmail, isBrandingReady, isWhiteLabel, isDefaultHost, showBrandSkeleton]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}

export { getBrandCheckoutName } from "../utils/checkoutBrand";
